(() => {
  const ROOT_SELECTOR = '[data-solvi-swym-pdp]';
  const BUTTON_SELECTOR = '[data-solvi-swym-pdp-button]';
  const LABEL_SELECTOR = '[data-solvi-swym-pdp-label]';
  const STATUS_SELECTOR = '[data-solvi-swym-pdp-status]';
  const VARIANTS_SELECTOR = '[data-solvi-swym-variants]';
  const READY_EVENT = 'solvi:swym-ready';
  const instances = new Set();
  let swym = window.solviSwymSdk || null;
  let swymVariantListenerAttached = false;

  const idString = (value) => String(value == null ? '' : value);

  const parseVariants = (root) => {
    const script = root.querySelector(VARIANTS_SELECTOR);
    if (!script) return [];

    try {
      const variants = JSON.parse(script.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      return [];
    }
  };

  const normalizeWishlistItems = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.products)) return response.products;
    return [];
  };

  const fetchWishlist = (onSuccess, onError) => {
    if (!swym || typeof swym.fetch !== 'function') {
      onError?.();
      return;
    }

    try {
      swym.fetch(
        (response) => onSuccess(normalizeWishlistItems(response)),
        () => onError?.()
      );
    } catch (error) {
      onError?.();
    }
  };

  const currentShopifyVariantId = (instance) => {
    const productInfo = instance.root.closest('product-info') || document.querySelector('product-info');
    const variantInput = productInfo?.querySelector('input.product-variant-id[name="id"]');
    return variantInput?.value || instance.root.dataset.fallbackVariantId;
  };

  const chooseWishlistVariantId = (instance) => {
    const selectedId = idString(currentShopifyVariantId(instance));
    const selectedVariant = instance.variants.find((variant) => idString(variant.id) === selectedId);

    if (selectedVariant && selectedVariant.isSample !== true) return idString(selectedVariant.id);

    const availableFullSize = instance.variants.find(
      (variant) => variant.available === true && variant.isSample !== true
    );

    return idString(availableFullSize?.id || instance.root.dataset.fallbackVariantId);
  };

  const productPayload = (instance) => {
    const payload = {
      empi: Number(instance.root.dataset.productId),
      epi: Number(instance.variantId),
      du: instance.root.dataset.productUrl
    };

    if (instance.root.dataset.productTitle) payload.dt = instance.root.dataset.productTitle;
    if (instance.root.dataset.productImage) payload.iu = instance.root.dataset.productImage;
    return payload;
  };

  const matchingItem = (items, instance) => items.find(
    (item) => idString(item.empi) === idString(instance.root.dataset.productId)
      && idString(item.epi) === idString(instance.variantId)
  );

  const renderState = (instance, saved, options = {}) => {
    const { busy = false, ready = true, message = '' } = options;
    instance.saved = saved;
    instance.button.classList.toggle('is-saved', saved);
    instance.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    instance.button.setAttribute('aria-busy', busy ? 'true' : 'false');
    instance.button.disabled = busy || !ready;
    instance.label.textContent = saved ? 'on my shelf' : 'add to my shelf';
    if (message) instance.status.textContent = message;
  };

  const refreshState = (instance) => {
    instance.variantId = chooseWishlistVariantId(instance);
    renderState(instance, false, { busy: true, ready: Boolean(swym) });

    fetchWishlist(
      (items) => {
        instance.savedItem = matchingItem(items, instance) || null;
        renderState(instance, Boolean(instance.savedItem), { ready: true });
      },
      () => {
        renderState(instance, instance.saved, {
          ready: false,
          message: 'Your shelf is temporarily unavailable. Please try again.'
        });
      }
    );
  };

  const toggleWishlist = (instance) => {
    if (!swym || instance.button.disabled) return;

    const action = instance.saved ? swym.removeFromWishList : swym.addToWishList;
    if (typeof action !== 'function') return;

    const nextSavedState = !instance.saved;
    const payload = instance.savedItem || productPayload(instance);
    renderState(instance, instance.saved, { busy: true, ready: true });

    try {
      action.call(
        swym,
        payload,
        () => {
          instance.savedItem = nextSavedState ? productPayload(instance) : null;
          renderState(instance, nextSavedState, {
            ready: true,
            message: nextSavedState ? 'Added to your shelf.' : 'Removed from your shelf.'
          });
        },
        () => {
          renderState(instance, instance.saved, {
            ready: true,
            message: 'We could not update your shelf. Please try again.'
          });
        }
      );
    } catch (error) {
      renderState(instance, instance.saved, {
        ready: true,
        message: 'We could not update your shelf. Please try again.'
      });
    }
  };

  const scheduleVariantRefresh = (instance) => {
    window.setTimeout(() => {
      const nextVariantId = chooseWishlistVariantId(instance);
      if (nextVariantId !== instance.variantId) refreshState(instance);
    }, 0);
  };

  const attachVariantListeners = (instance) => {
    const productInfo = instance.root.closest('product-info') || document.querySelector('product-info');
    if (!productInfo) return;

    productInfo.addEventListener('change', () => scheduleVariantRefresh(instance));
    ['variant:change', 'variant:changed', 'product:variant-change'].forEach((eventName) => {
      productInfo.addEventListener(eventName, () => scheduleVariantRefresh(instance));
    });
  };

  const attachSwymVariantListener = () => {
    if (swymVariantListenerAttached || !swym?.evtLayer || !swym?.JSEvents?.variantChanged) return;

    swymVariantListenerAttached = true;
    swym.evtLayer.addEventListener(swym.JSEvents.variantChanged, () => {
      instances.forEach(scheduleVariantRefresh);
    });
  };

  const initRoot = (root) => {
    if (root.dataset.solviSwymReady === 'true') return;

    const button = root.querySelector(BUTTON_SELECTOR);
    const label = root.querySelector(LABEL_SELECTOR);
    const status = root.querySelector(STATUS_SELECTOR);
    if (!button || !label || !status) return;

    root.dataset.solviSwymReady = 'true';
    const instance = {
      root,
      button,
      label,
      status,
      variants: parseVariants(root),
      variantId: '',
      saved: false,
      savedItem: null
    };

    instances.add(instance);
    button.addEventListener('click', () => toggleWishlist(instance));
    attachVariantListeners(instance);

    if (swym) refreshState(instance);
    else renderState(instance, false, { ready: false, busy: true });
  };

  const initAll = (container = document) => {
    if (container.matches?.(ROOT_SELECTOR)) initRoot(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  const acceptSwym = (candidate) => {
    if (!candidate || typeof candidate.fetch !== 'function') return;
    swym = candidate;
    attachSwymVariantListener();
    instances.forEach(refreshState);
  };

  window.addEventListener(READY_EVENT, (event) => acceptSwym(event.detail?.swat));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  acceptSwym(window.solviSwymSdk);
})();
